import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiTrash, HiXMark } from 'react-icons/hi2';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from '../../services/api';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);

const EMPTY_FORM = {
  name: '', phone: '', role: '', area_assigned: '', salary: '', joining_date: '',
  create_login: false, login_email: '', login_password: '',
};

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEmployees = useCallback(() => {
    setLoading(true);
    getEmployees()
      .then((res) => {
        const data = res.data;
        setEmployees(Array.isArray(data) ? data : data.employees || []);
      })
      .catch(() => toast.error('Failed to load employees.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const openAdd = () => { setEditEmployee(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (emp) => {
    setEditEmployee(emp);
    setForm({
      name: emp.name || '', phone: emp.phone || '', role: emp.role || '',
      area_assigned: emp.area_assigned || '', salary: emp.salary ?? '',
      joining_date: emp.joining_date ? emp.joining_date.split('T')[0] : '',
      create_login: false, login_email: '', login_password: '',
    });
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditEmployee(null); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error('Name and Phone are required.'); return; }
    if (form.create_login && !editEmployee && (!form.login_email.trim() || !form.login_password.trim())) {
      toast.error('Email and Password required for login account.');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name, phone: form.phone, role: form.role,
      area_assigned: form.area_assigned,
      salary: form.salary !== '' ? Number(form.salary) : undefined,
      joining_date: form.joining_date || undefined,
      ...(form.create_login && !editEmployee ? {
        create_login: true, login_email: form.login_email, login_password: form.login_password,
      } : {}),
    };
    try {
      if (editEmployee) {
        await updateEmployee(editEmployee.id || editEmployee._id, payload);
        toast.success('Employee updated.');
      } else {
        await createEmployee(payload);
        toast.success('Employee added.');
      }
      closeModal();
      fetchEmployees();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save employee.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEmployee(deleteTarget.id || deleteTarget._id);
      toast.success('Employee deleted.');
      setDeleteTarget(null);
      fetchEmployees();
    } catch {
      toast.error('Failed to delete employee.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="content-area">
      <div className="page-header">
        <h1 className="page-title">Employees</h1>
        <button className="btn btn-primary" onClick={openAdd}><HiPlus /> Add Employee</button>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Area Assigned</th>
                  <th>Salary Rs.</th>
                  <th>Joining Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8' }}>No employees found.</td></tr>
                ) : (
                  employees.map((emp) => (
                    <tr key={emp.id || emp._id}>
                      <td style={{ fontWeight: 500 }}>{emp.name}</td>
                      <td>{emp.phone}</td>
                      <td>{emp.role || '-'}</td>
                      <td>{emp.area_assigned || '-'}</td>
                      <td>Rs.{fmt(emp.salary)}</td>
                      <td>{emp.joining_date ? new Date(emp.joining_date).toLocaleDateString('en-IN') : '-'}</td>
                      <td>
                        <span className={`badge ${emp.status === 'active' || emp.is_active !== false ? 'badge-green' : 'badge-gray'}`}>
                          {emp.status || (emp.is_active !== false ? 'active' : 'inactive')}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(emp)}><HiPencil /> Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(emp)}><HiTrash /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: '580px' }}>
            <div className="modal-header">
              <span className="modal-title">{editEmployee ? 'Edit Employee' : 'Add Employee'}</span>
              <button className="btn btn-secondary btn-sm" onClick={closeModal}><HiXMark /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input className="form-control" name="name" value={form.name} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input className="form-control" name="phone" value={form.phone} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <input className="form-control" name="role" value={form.role} onChange={handleChange} placeholder="e.g. Delivery Staff" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Area Assigned</label>
                    <input className="form-control" name="area_assigned" value={form.area_assigned} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Salary (Rs.)</label>
                    <input className="form-control" type="number" name="salary" value={form.salary} onChange={handleChange} min="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Joining Date</label>
                    <input className="form-control" type="date" name="joining_date" value={form.joining_date} onChange={handleChange} />
                  </div>
                </div>

                {!editEmployee && (
                  <>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" id="emp_create_login" name="create_login" checked={form.create_login} onChange={handleChange} />
                      <label htmlFor="emp_create_login" style={{ cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
                        Create Login Account
                      </label>
                    </div>
                    {form.create_login && (
                      <div className="grid grid-2">
                        <div className="form-group">
                          <label className="form-label">Login Email *</label>
                          <input className="form-control" type="email" name="login_email" value={form.login_email} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Password *</label>
                          <input className="form-control" type="password" name="login_password" value={form.login_password} onChange={handleChange} />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editEmployee ? 'Update' : 'Add Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <span className="modal-title">Delete Employee</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget(null)}><HiXMark /></button>
            </div>
            <div className="modal-body">
              <p>Delete employee <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
